/**
 * BenchClearers CRO event tracking — fires GA4 custom events for the
 * recommendations + filter drawer instrumentation needed by the bi-weekly
 * CRO Impact Audit.
 *
 * Events emitted (all to GA4 via gtag):
 *   - view_item_recommendations  : "Fans Also Rep" enters viewport (once per page)
 *   - select_recommendation      : User clicks a rec product card
 *   - view_filter_drawer         : Filter drawer opens
 *   - apply_filter               : User toggles a filter checkbox on, or picks an Audience/League button
 *   - clear_all_filters          : User clicks the "Clear all filters" button or the buttons' "Clear all"
 *   - add_to_cart_from_recommendation : ATC happens within 30 min of a rec click
 *   - team_story_open            : User opens a team story modal (collection hero or PDP pill)
 *   - nav_menu_open              : Header menu opened (desktop dropdown kept open 500 ms; mobile drawer / audience tab)
 *   - nav_category_view          : Menu category shown (desktop gender rail / SHOP BY LEAGUE tab; mobile category slide)
 *   - nav_click                  : Any link clicked in the header menu or mobile drawer
 *
 * apply_filter carries filter_source: 'chips' (Audience/League buttons) or 'drawer' (filter drawer checkboxes).
 *
 * Self-contained, no dependencies. Idempotent — listeners are added once.
 * Safe to load on any page (handlers exit cleanly when target elements absent).
 */
(function () {
  'use strict';

  // Bail if gtag isn't loaded for some reason
  if (typeof gtag !== 'function') {
    console.warn('[cro-events] gtag not found; events will not fire');
    return;
  }

  var REC_CLICK_KEY = 'bc_rec_click';
  var REC_CLICK_TTL_MS = 30 * 60 * 1000;  // 30 min window for "from rec" ATC

  // ------------- 1. view_item_recommendations -------------
  function trackRecsViewport() {
    var recsSection = document.querySelector('product-recommendations, .product-recommendations, [data-section-type="product-recommendations"]');
    if (!recsSection) return;

    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !fired) {
          fired = true;
          gtag('event', 'view_item_recommendations', {
            page_location: window.location.pathname,
            recs_count: recsSection.querySelectorAll('.grid__item, .product-card').length || null,
          });
          io.disconnect();
        }
      });
    }, { threshold: 0.4 });
    io.observe(recsSection);
  }

  // ------------- 2. select_recommendation -------------
  function trackRecsClicks() {
    var recsSection = document.querySelector('product-recommendations, .product-recommendations, [data-section-type="product-recommendations"]');
    if (!recsSection) return;

    recsSection.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      var handle = (link.getAttribute('href').match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle) return;

      gtag('event', 'select_recommendation', {
        item_id: handle,
        page_location: window.location.pathname,
        link_text: (link.textContent || '').trim().slice(0, 80),
      });

      // Stash for add_to_cart_from_recommendation tracking
      try {
        sessionStorage.setItem(REC_CLICK_KEY, JSON.stringify({
          handle: handle,
          ts: Date.now(),
        }));
      } catch (_) { /* sessionStorage may be disabled */ }
    });
  }

  // ------------- 3. view_filter_drawer -------------
  function trackFilterDrawerOpen() {
    var trigger = document.querySelector('#FilterDrawerTrigger, .js-drawer-open-collection-filters');
    if (!trigger) return;

    trigger.addEventListener('click', function () {
      gtag('event', 'view_filter_drawer', {
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 4. apply_filter -------------
  function trackFilterApply() {
    var drawer = document.querySelector('#FilterDrawer');
    if (!drawer) return;

    drawer.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.type !== 'checkbox') return;
      if (!input.classList.contains('tag__input')) return;
      // Only fire on toggle-on (apply), not toggle-off (remove)
      if (!input.checked) return;

      var name = input.getAttribute('name') || '';
      var value = input.getAttribute('value') || '';
      gtag('event', 'apply_filter', {
        filter_name: name,
        filter_value: value,
        filter_type: name.replace('filter.p.m.custom.', '').replace('filter.p.', ''),
        filter_source: 'drawer',
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 5. clear_all_filters -------------
  function trackClearAll() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.filter-clear-all-btn');
      if (!btn) return;
      // Count active filters from the drawer for context
      var activeChips = document.querySelectorAll('#FilterDrawer .tag--remove').length || null;
      gtag('event', 'clear_all_filters', {
        active_filters_cleared: activeChips,
        page_location: window.location.pathname,
      });
    });
  }

  // ------------- 6. add_to_cart_from_recommendation -------------
  function trackAtcFromRec() {
    function checkAndFire() {
      var stash;
      try { stash = JSON.parse(sessionStorage.getItem(REC_CLICK_KEY) || 'null'); } catch (_) { return; }
      if (!stash || !stash.handle || !stash.ts) return;
      if (Date.now() - stash.ts > REC_CLICK_TTL_MS) return;

      // Match: are we currently on the product the rec sent us to?
      var currentHandle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (currentHandle !== stash.handle) return;

      gtag('event', 'add_to_cart_from_recommendation', {
        item_id: currentHandle,
        rec_click_age_ms: Date.now() - stash.ts,
      });

      // Clear so we don't double-fire on same rec click
      try { sessionStorage.removeItem(REC_CLICK_KEY); } catch (_) {}
    }

    // Path 1: form submit (works on themes without AJAX cart)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.action === undefined) return;
      if (!/\/cart\/add/.test(form.action)) return;
      checkAndFire();
    });

    // Path 2: wrap fetch — catches AJAX ATC (Motion theme + Upcart use this)
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var url = arguments[0];
        var urlStr = typeof url === 'string' ? url : (url && url.url) || '';
        var isAtc = /\/cart\/add(\.js)?(\?|$)/.test(urlStr);
        var result = origFetch.apply(this, arguments);
        if (isAtc) {
          result.then(function (resp) {
            if (resp && resp.ok) checkAndFire();
          }).catch(function () { /* swallow */ });
        }
        return result;
      };
    }

    // Path 3: wrap XHR — fallback for any code path using XMLHttpRequest
    if (window.XMLHttpRequest) {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this._croAtcUrl = url;
        return origOpen.apply(this, arguments);
      };
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (xhr._croAtcUrl && /\/cart\/add(\.js)?(\?|$)/.test(xhr._croAtcUrl)) {
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) checkAndFire();
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  // ------------- 7. team_story_open -------------
  function trackTeamStoryOpen() {
    // Capture phase: <tool-tip-trigger> calls stopPropagation() on the click,
    // so a bubble-phase listener here would never see it.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.team-story-trigger__btn');
      if (!btn) return;

      gtag('event', 'team_story_open', {
        story_id: btn.getAttribute('data-ts-story') || null,
        surface: btn.classList.contains('team-story-trigger__btn--collection') ? 'collection' : 'product',
        page_location: window.location.pathname,
      });
    }, true);
  }

  // ------------- 8. Audience / League button rows (apply_filter, clear_all_filters) -------------
  function trackCollectionChips() {
    // Capture phase on document: the rows sit inside #CollectionAjaxContent, which AJAX filtering replaces, and the
    // theme's own click handler removes a clicked "Clear all" from the DOM before a bubble-phase listener would see it.
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('.bc-chips a');
      if (!link) return;

      if (link.hasAttribute('data-clear-all')) {
        gtag('event', 'clear_all_filters', {
          active_filters_cleared: parseInt(link.getAttribute('data-active-count'), 10) || null,
          page_location: window.location.pathname,
        });
        return;
      }

      // Only options that add a value carry data-filter-name; All / selected league / pills only remove one.
      var name = link.getAttribute('data-filter-name');
      if (!name) return;
      gtag('event', 'apply_filter', {
        filter_name: name,
        filter_value: link.getAttribute('data-filter-value') || '',
        filter_type: name.replace('filter.p.m.custom.', ''),
        filter_source: link.getAttribute('data-filter-source') || 'chips',
        page_location: window.location.pathname,
      });
    }, true);
  }

  // ------------- 9. Header navigation (nav_menu_open, nav_category_view, nav_click) -------------
  // Desktop: #SiteHeader .site-nav (top-level <details> dropdowns, MEN'S/WOMEN'S/YOUTH panels [data-gender-panel],
  // SHOP BY LEAGUE tabs). Mobile: #NavDrawer (audience tabs + category slides [data-drawer-gender], accordions).
  // GA4 caps parameter values at 100 characters.
  function trackNavigation() {
    var OPEN_DWELL_MS = 500;   // hover-opened dropdowns the pointer only passes through are not "opens"

    function clean(text, max) {
      // drop the "387 styles" counts the panels print next to categories
      return (text || '').replace(/\s+/g, ' ').replace(/\s*\b\d+ styles?\b/gi, '').trim().slice(0, max || 100) || null;
    }

    function linkPath(href) {
      try {
        var u = new URL(href, window.location.origin);
        return (u.origin === window.location.origin ? u.pathname + u.search : u.href).slice(0, 100);
      } catch (_) { return null; }
    }

    function topTitle(item) {
      var top = item && item.querySelector('.site-nav__link');
      return top ? clean(top.textContent, 40) : null;
    }

    function drawerOpen() {
      var drawer = document.getElementById('NavDrawer');
      return !!drawer && drawer.classList.contains('drawer--is-open');
    }

    function drawerTabTitle(panel) {
      var tab = panel && document.getElementById(panel.getAttribute('aria-labelledby'));
      return tab ? clean(tab.textContent, 40) : null;
    }

    // nav_menu_open (desktop): <details> toggle does not bubble, so listen in the capture phase.
    document.addEventListener('toggle', function (e) {
      var details = e.target;
      if (!details.matches || !details.matches('#SiteHeader .site-nav__details') || !details.open) return;
      setTimeout(function () {
        if (!details.open) return;
        gtag('event', 'nav_menu_open', {
          nav_menu: topTitle(details.closest('.site-nav__item')),
          device: 'desktop',
        });
      }, OPEN_DWELL_MS);
    }, true);

    // nav_category_view (desktop gender rail): tabs switch by click, hover intent or arrow keys (assets/nav-gender.js),
    // so watch aria-selected. Once per category per open; the reset to the first tab on close is not a view.
    if (window.MutationObserver) {
      Array.prototype.forEach.call(document.querySelectorAll('#SiteHeader [data-gender-panel]'), function (root) {
        var details = root.closest('details');
        var seen = {};
        if (details) {
          details.addEventListener('toggle', function () { if (!details.open) seen = {}; });
        }
        new MutationObserver(function (mutations) {
          if (!details || !details.open) return;
          mutations.forEach(function (m) {
            var tab = m.target;
            if (tab.getAttribute('aria-selected') !== 'true') return;
            var category = clean(tab.textContent, 60);
            if (seen[category]) return;
            seen[category] = true;
            gtag('event', 'nav_category_view', {
              nav_menu: topTitle(root.closest('.site-nav__item')),
              nav_category: category,
              device: 'desktop',
            });
          });
        }).observe(root, { attributes: true, attributeFilter: ['aria-selected'], subtree: true });
      });

      // nav_menu_open (mobile): the drawer opening
      var drawer = document.getElementById('NavDrawer');
      if (drawer) {
        var wasOpen = drawerOpen();
        new MutationObserver(function () {
          var open = drawerOpen();
          if (open && !wasOpen) gtag('event', 'nav_menu_open', { nav_menu: 'Menu drawer', device: 'mobile' });
          wasOpen = open;
        }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      // SHOP BY LEAGUE tab (theme.js switches these on click)
      var leagueTab = e.target.closest('#SiteHeader .megamenu__title_parent a');
      if (leagueTab) {
        gtag('event', 'nav_category_view', {
          nav_menu: topTitle(leagueTab.closest('.site-nav__item')),
          nav_category: clean(leagueTab.textContent, 60),
          device: 'desktop',
        });
        return;
      }

      // Mobile drawer: an audience tab or a category row (user actions only)
      var dgTab = e.target.closest('#NavDrawer [data-drawer-gender] [role="tab"]');
      if (dgTab) {
        if (dgTab.getAttribute('aria-selected') !== 'true') {
          gtag('event', 'nav_menu_open', { nav_menu: clean(dgTab.textContent, 40), device: 'mobile' });
        }
        return;
      }
      var dgRow = e.target.closest('#NavDrawer .drawer-gender__row');
      if (dgRow) {
        gtag('event', 'nav_category_view', {
          nav_menu: drawerTabTitle(dgRow.closest('.drawer-gender__panel')),
          nav_category: clean((dgRow.querySelector('.drawer-gender__name') || dgRow).textContent, 60),
          device: 'mobile',
        });
        return;
      }

      // nav_click: any link in the desktop menu or the mobile drawer
      var link = e.target.closest('#SiteHeader .site-nav a[href], #SiteHeader .site-nav summary, #NavDrawer a[href]');
      if (!link) return;
      var mobile = !!link.closest('#NavDrawer');
      var params = { nav_item: clean(link.textContent), link_url: linkPath(link.getAttribute('href') || link.getAttribute('data-link')), device: mobile ? 'mobile' : 'desktop' };
      var type = 'link';
      if (mobile) {
        var panel = link.closest('.drawer-gender__panel');
        var slide = link.closest('.drawer-gender__slide');
        if (panel) {
          params.nav_menu = drawerTabTitle(panel);
        } else {
          var top = link.closest('ul.mobile-nav > li');
          var topLink = top && top.querySelector('.mobile-nav__faux-link, .mobile-nav__link');
          params.nav_menu = topLink ? clean(topLink.textContent, 40) : null;
        }
        if (slide) params.nav_category = clean(slide.getAttribute('data-nav-category'), 60);
        if (link.matches('.drawer-gender__league')) type = 'league_tile';
        else if (link.matches('.drawer-gender__slide-all')) type = 'category_all';
        else if (link.matches('.drawer-gender__all')) type = 'shop_all';
        else if (link.closest('.drawer-gender__trend')) type = 'trending';
      } else {
        var item = link.closest('.site-nav__item');
        var gpPanel = link.closest('.gp-panel');
        var col = link.closest('.megamenu__col_new');
        params.nav_menu = topTitle(item);
        if (gpPanel) params.nav_category = clean(gpPanel.getAttribute('data-nav-category'), 60);
        else if (col && col.id) {
          var colTab = document.querySelector('#SiteHeader [data-id="' + col.id + '"]');
          if (colTab) params.nav_category = clean(colTab.textContent, 60);
        }
        if (link.matches('summary') || link.matches('.site-nav__item > a.site-nav__link')) type = 'top_level';
        else if (link.matches('.gp-tile--all')) type = 'category_all';
        else if (link.matches('.gp-tile')) type = 'league_tile';
        else if (link.matches('.gp-shopall')) type = 'category_all';
        else if (link.matches('.gp-rail__all')) type = 'shop_all';
        else if (link.matches('.gp-feature__media')) {
          type = 'feature';
          var featureImg = link.querySelector('img[alt]');
          if (featureImg) params.nav_item = clean(featureImg.getAttribute('alt'));   // the product, not the "Best seller" badge
        }
        else if (link.closest('.gp-feature')) type = 'feature_shop_all';   // label / "Shop now" -> the category, best-selling
        else if (link.closest('.gp-trend')) {
          type = 'trending';
          var trendName = link.querySelector('span');
          if (trendName) params.nav_item = clean(trendName.textContent);   // the product, without the price span
        }
        else if (link.closest('.gp-quick')) type = 'quick_link';
      }
      params.nav_type = type;
      gtag('event', 'nav_click', params);
    }, true);
  }

  // ------------- Boot -------------
  function init() {
    trackRecsViewport();
    trackRecsClicks();
    trackFilterDrawerOpen();
    trackFilterApply();
    trackClearAll();
    trackAtcFromRec();
    trackTeamStoryOpen();
    trackCollectionChips();
    trackNavigation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
